import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useOrgData } from '../hooks/useOrgData';
import { loadPipeline } from '../lib/dashboard';
import type { Phase, ProductSummary } from '../shared/types';
import { StatusBadge, desvioLabel, fmtDate } from '../components/ui';

const PHASES: Phase[] = ['PD1', 'PD2', 'PD3', 'PD4', 'PD5'];
const PHASE_NOME: Record<Phase, string> = {
  PD1: 'PD1 · Concepção',
  PD2: 'PD2 · Aprovação',
  PD3: 'PD3 · Desenvolvimento',
  PD4: 'PD4 · Industrialização',
  PD5: 'PD5 · Lançamento',
};
const brl = (n: number) =>
  n >= 1e6
    ? `R$ ${(n / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} mi`
    : `R$ ${n.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;

export function Dashboard() {
  const { currentOrgId } = useAuth();
  const { loading, summaries } = useOrgData(currentOrgId);
  const nav = useNavigate();
  const [pipeline, setPipeline] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    if (currentOrgId) loadPipeline(currentOrgId).then(setPipeline);
  }, [currentOrgId]);

  const ativos = useMemo(
    () => summaries.filter((s) => s.lifecycle === 'ativo' || s.lifecycle === 'pausado'),
    [summaries],
  );
  const receitaDe = (s: ProductSummary) => pipeline.get(s.productId) ?? 0;

  const kpis = useMemo(() => {
    const atrasados = ativos.filter((s) => s.status === 'atrasado').length;
    const atencao = ativos.filter((s) => s.status === 'atencao').length;
    const comMeta = ativos.filter((s) => s.targetLaunch);
    const noPrazoMeta = comMeta.filter((s) => (s.desvioMeta ?? 0) <= 0).length;
    const onTime = comMeta.length ? Math.round((noPrazoMeta / comMeta.length) * 100) : null;
    const pipelineTotal = ativos.reduce((a, s) => a + receitaDe(s), 0);
    const concluidos = summaries.filter((s) => s.lifecycle === 'concluido').length;
    const cancelados = summaries.filter((s) => s.lifecycle === 'cancelado').length;
    return { atrasados, atencao, onTime, pipelineTotal, concluidos, cancelados };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ativos, pipeline, summaries]);

  const byPhase = useMemo(() => {
    const m = new Map<Phase, number>(PHASES.map((p) => [p, 0]));
    for (const s of ativos) {
      const f = s.faseAtual;
      if (f !== 'Concluído' && f !== '—') m.set(f, (m.get(f) ?? 0) + 1);
    }
    return m;
  }, [ativos]);
  const maxPhase = Math.max(1, ...byPhase.values());

  const byBrand = useMemo(() => {
    const m = new Map<string, { n: number; receita: number; atrasados: number }>();
    for (const s of ativos) {
      const cur = m.get(s.brand) ?? { n: 0, receita: 0, atrasados: 0 };
      cur.n += 1;
      cur.receita += receitaDe(s);
      if (s.status === 'atrasado') cur.atrasados += 1;
      m.set(s.brand, cur);
    }
    return [...m.entries()].sort((a, b) => b[1].receita - a[1].receita);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ativos, pipeline]);

  const emRisco = useMemo(
    () =>
      ativos
        .filter((s) => s.status === 'atrasado' || s.status === 'atencao')
        .sort((a, b) => b.desvioDias - a.desvioDias)
        .slice(0, 10),
    [ativos],
  );

  return (
    <>
      <div className="topbar">
        <h1>Painel Executivo</h1>
        <div className="stat-strip">
          <div className="stat-chip">
            <span className="n tabular">{ativos.length}</span>
            <span className="l">Projetos ativos</span>
          </div>
          <div className={`stat-chip ${kpis.atrasados ? 'warn' : ''}`}>
            <span className="n tabular">{kpis.atrasados}</span>
            <span className="l">Atrasados</span>
          </div>
          <div className="stat-chip">
            <span className="n tabular">{kpis.onTime == null ? '—' : `${kpis.onTime}%`}</span>
            <span className="l">No prazo da meta</span>
          </div>
          <div className="stat-chip">
            <span className="n tabular" style={{ fontSize: 15 }}>{brl(kpis.pipelineTotal)}</span>
            <span className="l">Pipeline 12m (ROB)</span>
          </div>
        </div>
      </div>

      <div className="content">
        {loading ? (
          <div className="center-note">Carregando…</div>
        ) : (
          <>
            {/* Funil por fase */}
            <div className="card card-pad">
              <div className="section-title">
                <h2>Funil por fase (projetos ativos)</h2>
                <span className="muted" style={{ fontSize: 12 }}>{ativos.length} projeto(s)</span>
              </div>
              <div className="funnel">
                {PHASES.map((p) => {
                  const n = byPhase.get(p) ?? 0;
                  return (
                    <div className="funnel-row" key={p}>
                      <div className="funnel-lbl">{PHASE_NOME[p]}</div>
                      <div className="funnel-track">
                        <i style={{ width: `${(n / maxPhase) * 100}%` }} />
                      </div>
                      <div className="funnel-n tabular">{n}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="dash-2col">
              {/* Por marca */}
              <div className="card card-pad">
                <div className="section-title"><h2>Por marca</h2></div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr><th className="l">Marca</th><th>Ativos</th><th>Atrasados</th><th>Pipeline 12m</th></tr>
                    </thead>
                    <tbody>
                      {byBrand.map(([brand, v]) => (
                        <tr key={brand}>
                          <td className="l" style={{ fontWeight: 600 }}>{brand}</td>
                          <td className="tabular">{v.n}</td>
                          <td className="tabular" style={{ color: v.atrasados ? 'var(--st-atrasada)' : 'inherit' }}>{v.atrasados}</td>
                          <td className="tabular">{brl(v.receita)}</td>
                        </tr>
                      ))}
                      {byBrand.length === 0 && <tr><td colSpan={4} className="muted">Sem projetos ativos.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Em risco */}
              <div className="card card-pad">
                <div className="section-title">
                  <h2>Projetos em risco</h2>
                  <span className="muted" style={{ fontSize: 12 }}>atrasados e em atenção</span>
                </div>
                {emRisco.length === 0 ? (
                  <p className="muted">Nenhum projeto em risco. 👏</p>
                ) : (
                  <div className="risk-list">
                    {emRisco.map((s) => (
                      <button key={s.productId} className="risk-row" onClick={() => nav(`/produto/${s.productId}`)}>
                        <div className="risk-main">
                          <div className="risk-name">{s.brand} · {s.name}</div>
                          <div className="muted" style={{ fontSize: 12 }}>
                            {s.faseAtual} · previsão {fmtDate(s.previsaoConclusao)}
                          </div>
                        </div>
                        <div className="risk-right">
                          <StatusBadge status={s.status} />
                          <span className="tabular" style={{ color: 'var(--st-atrasada)', fontSize: 12, fontWeight: 700 }}>
                            {desvioLabel(s.desvioDias)}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="dash-foot muted">
              {kpis.concluidos} concluído(s) · {kpis.cancelados} cancelado(s) no histórico.
            </div>
          </>
        )}
      </div>
    </>
  );
}
